import React, { useCallback, useEffect, useState } from "react";
import { Alert, Button, List, Segmented, Space, Tag, Tooltip, Typography } from "antd";
import LoadingCard from "components/LoadingCard/LoadingCard";
import ListSearch from "components/ListSearch/ListSearch";
import { useTextFilter } from "helpers/textFilter";
import { LANGUAGES } from "./languages";
import PageEditor from "./PageEditor";
import { errorMessage, fetchManifests, fetchPublishedPages } from "./pagesApi";

const { Title, Text } = Typography;

const manifestSearchFields = (manifest) => [manifest.label, manifest.route];


/**
 * The marketing pages Gloria can edit.
 *
 * The list is driven by manifests, which are generated from the typed content
 * modules in hpn-landing and pushed by its sync script — admin cannot create,
 * delete or restructure a page here. Adding one is a dev task, by design.
 */
export default function MarketingPages() {
  const [lang, setLang] = useState("bg");
  const [manifests, setManifests] = useState([]);
  const [publishedKeys, setPublishedKeys] = useState([]);
  const [openPageKey, setOpenPageKey] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  /**
   * Manifests are structure, and structure is language-independent — so the
   * list is fetched once and the language switch only refreshes which pages are
   * published. Loading both together re-downloaded ~200 field definitions every
   * time Gloria toggled BG/EN.
   */
  const loadPublished = useCallback(async () => {
    try {
      // One public read covers every page's published state, rather than one
      // editor request per row.
      const published = await fetchPublishedPages(lang);
      setPublishedKeys(Object.keys(published || {}));
    } catch (error) {
      setLoadError(errorMessage(error));
    }
  }, [lang]);

  const loadManifests = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      setManifests(await fetchManifests());
    } catch (error) {
      setLoadError(errorMessage(error));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadManifests();
  }, [loadManifests]);

  useEffect(() => {
    loadPublished();
  }, [loadPublished]);

  const visibleManifests = useTextFilter(manifests, manifestSearchFields, searchQuery);

  if (openPageKey) {
    return (
      <PageEditor
        pageKey={openPageKey}
        lang={lang}
        onBack={() => setOpenPageKey(null)}
        onStatusChange={loadPublished}
        // The editor switches language without leaving the screen, but the
        // state stays here: the list's own Segmented reads the same value and
        // cannot fall out of step with it.
        onLangChange={setLang}
      />
    );
  }

  return (
    <div style={{ padding: 16, maxWidth: 720, margin: "0 auto" }}>
      <Space direction="vertical" size={4} style={{ width: "100%", marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          Маркетингови страници
        </Title>
        <Text type="secondary">
          Редактираш текста на публичния сайт. Полетата, които оставиш празни, пазят
          вградения в сайта текст.
        </Text>
      </Space>

      <Segmented
        options={LANGUAGES}
        value={lang}
        onChange={setLang}
        block
        style={{ marginBottom: 16 }}
      />

      {isLoading && <LoadingCard />}

      {!isLoading && !loadError && (
        <ListSearch
          value={searchQuery}
          onSearch={setSearchQuery}
          placeholder="Търсене по заглавие или адрес"
          className="mb-3"
        />
      )}

      {!isLoading && loadError && (
        <Alert
          type="error"
          showIcon
          message="Неуспешно зареждане на страниците"
          description={loadError}
          action={
            <Button
              onClick={() => {
                loadManifests();
                loadPublished();
              }}
            >
              Опитай отново
            </Button>
          }
        />
      )}

      {!isLoading && !loadError && (
        <List
          bordered
          dataSource={visibleManifests}
          locale={{
            emptyText: searchQuery
              ? "Няма страници, отговарящи на търсенето."
              : "Все още няма страници — пусни синхронизацията на manifest в hpn-landing, за да се появят тук.",
          }}
          renderItem={(manifest) => (
            <List.Item
              style={{ cursor: "pointer", minHeight: 64 }}
              onClick={() => setOpenPageKey(manifest.pageKey)}
            >
              <List.Item.Meta
                title={
                  <Space wrap size={8}>
                    <span>{manifest.label}</span>
                    {publishedKeys.includes(manifest.pageKey) ? (
                      <Tooltip title="Сайтът показва публикуваните ти редакции.">
                        <Tag color="green">Редактирана</Tag>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Страницата е на сайта с текста по подразбиране — още няма публикувани редакции.">
                        <Tag>Текст от сайта</Tag>
                      </Tooltip>
                    )}
                  </Space>
                }
                description={<Text type="secondary">{manifest.route}</Text>}
              />
            </List.Item>
          )}
        />
      )}
    </div>
  );
}
